import { CommonModule } from '@angular/common';
import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { AlertController, IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { AdvancedModelSettingsService } from '../../../core/services/advanced-model-settings.service';
import {
  BidirectionalConversationService,
  BidirectionalTranscript,
} from '../../../core/services/bidirectional-conversation.service';
import { TranslationService } from '../../../core/services/translation.service';
import { TranslatorService } from '../../../core/services/translator.service';

@Component({
  selector: 'app-bidirectional-conversation-modal',
  templateUrl: './bidirectional-conversation-modal.component.html',
  styleUrls: ['./bidirectional-conversation-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
})
export class BidirectionalConversationModalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() isOpen = false;
  @Input() sourceLanguage: string = 'en';
  @Input() targetLanguage: string = 'en';
  @Input() conversationTopic: string = '';
  @Output() close = new EventEmitter<void>();
  @Output() showBuyWords = new EventEmitter<void>();

  selectedSourceLanguage: string = 'en';
  selectedTargetLanguage: string = 'en';
  recording = false;
  transcripts: BidirectionalTranscript[] = [];
  errorMessage = '';
  clickedButton: 'start' | 'stop' | 'clear' | null = null;
  statusText: string = 'Ready to transcribe';
  useAdvancedModel = false;

  // Platform detection
  isAndroid = Capacitor.getPlatform() === 'android';
  isIOSBrowser = this.detectIOSBrowser();

  private detectIOSBrowser(): boolean {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') return false; // Native iOS app
    if (platform === 'web') {
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    }
    return false;
  }

  // Audio feature
  audioEnabled = signal<boolean>(false);
  private audioQueue: Array<{ audioUrl: string; text: string }> = [];
  private queueCheckInterval: any = null;
  private isPlayingAudio = false;
  private currentAudio?: HTMLAudioElement;
  private processedTranscriptIndices = new Set<number>();
  private processedAudioTexts = new Set<string>();
  private isDismissing = false; // Flag to prevent audio playback when modal is closing

  private recordingSub?: Subscription;
  private transcriptsSub?: Subscription;
  private errorSub?: Subscription;
  private translationSub?: Subscription;
  translations: any = {};

  constructor(
    private readonly bidirectionalConversation: BidirectionalConversationService,
    private readonly advancedModelSettings: AdvancedModelSettingsService,
    private readonly translationService: TranslationService,
    private readonly translatorService: TranslatorService,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly appRef: ApplicationRef,
    private readonly alertController: AlertController
  ) {
    this.recordingSub = this.bidirectionalConversation.isRecording.subscribe(
      (isRecording) => {
        this.ngZone.run(() => {
          this.recording = isRecording;
          this.updateStatusText();
          this.cdr.markForCheck();

          if (this.clickedButton === 'start' && isRecording) {
            setTimeout(() => {
              this.clickedButton = null;
              this.cdr.markForCheck();
            }, 500);
          } else if (this.clickedButton === 'stop' && !isRecording) {
            setTimeout(() => {
              this.clickedButton = null;
              this.cdr.markForCheck();
            }, 500);
          }
        });
      }
    );

    this.transcriptsSub = this.bidirectionalConversation.transcripts.subscribe(
      (transcripts) => {
        this.transcripts = [...transcripts].reverse();

        if (this.audioEnabled()) {
          this.processNewTranscriptsForAudio(transcripts);
        } else {
          transcripts.forEach((t) =>
            this.processedTranscriptIndices.add(t.chunkIndex)
          );
        }

        this.cdr.markForCheck();

        if (
          this.clickedButton === 'clear' &&
          transcripts.length === 0
        ) {
          setTimeout(() => {
            this.clickedButton = null;
            this.cdr.markForCheck();
          }, 500);
        }
      }
    );

    this.errorSub = this.bidirectionalConversation.error.subscribe((error) => {
      this.errorMessage = error || '';
      this.cdr.markForCheck();
    });

    this.translations = this.translationService.getAllTranslations();
    this.updateStatusText();
    this.translationSub = this.translationService.currentLanguage$.subscribe(
      () => {
        this.translations = this.translationService.getAllTranslations();
        this.updateStatusText();
        this.cdr.markForCheck();
      }
    );
  }

  ngOnInit(): void {
    this.selectedSourceLanguage = this.sourceLanguage || 'en';
    this.selectedTargetLanguage = this.targetLanguage || 'en';
    this.updateStatusText();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sourceLanguage']) {
      this.selectedSourceLanguage = this.sourceLanguage || 'en';
    }

    if (changes['targetLanguage']) {
      this.selectedTargetLanguage = this.targetLanguage || 'en';
    }

    if (changes['isOpen']) {
      if (changes['isOpen'].currentValue) {
        // Modal opening - reset dismiss flag to allow audio playback
        this.isDismissing = false;
      } else {
        // Modal closing - cleanup
        this.isDismissing = true;
        this.stopAllAudio();
        this.stopQueueMonitoring();
        this.processedTranscriptIndices.clear();
        this.processedAudioTexts.clear();
        // Clear conversation history when modal closes
        this.bidirectionalConversation.clearTranscripts();
      }
    }
  }

  private updateStatusText(): void {
    this.statusText = this.recording
      ? this.translations.transcribing || 'Transcribing...'
      : this.translations.readyToTranscribe || 'Ready to transcribe';
  }

  isGlobalAdvancedModelEnabled(): boolean {
    return this.advancedModelSettings.isGlobalAdvancedModelEnabled();
  }

  isAdvancedModelEnabled(): boolean {
    return this.advancedModelSettings.getEffectiveUseAdvancedModel(
      this.useAdvancedModel
    );
  }

  onAdvancedModelChange(enabled: boolean): void {
    if (!this.isGlobalAdvancedModelEnabled()) {
      this.useAdvancedModel = enabled;
    }
  }

  async showAdvancedModelInfo(event: Event): Promise<void> {
    event.stopPropagation();
    const message =
      this.translations.advancedModelDescription ||
      'Uses gpt-5.4-mini. May be slower.';

    const alert = await this.alertController.create({
      header: this.translations.advancedModel || 'Advanced translation model',
      message,
      buttons: [
        {
          text: this.translations.ok || 'OK',
          role: 'cancel',
        },
      ],
    });
    await alert.present();
  }

  async startRecording(): Promise<void> {
    this.errorMessage = '';
    this.clickedButton = 'start';

    const sourceLang = this.sourceLanguage || this.selectedSourceLanguage || 'en';
    const targetLang = this.targetLanguage || this.selectedTargetLanguage || 'en';

    const topic = this.conversationTopic?.trim() || '';
    const systemPrompt = topic
      ? `\n\nCONVERSATION CONTEXT:\nThis conversation is about: "${topic}". The topic may be in any language - understand it regardless of the language used.\n\n🚨 CRITICAL RULES:\n- Use domain-specific terminology and context to provide more accurate transcriptions and translations\n- Translate ONLY what the user ACTUALLY SPOKE - DO NOT create stories or add information\n- DO NOT expand on the topic or generate examples/explanations\n- When translating, prioritize terms and phrases relevant to this topic, but translate ONLY the actual speech\n- The topic is for understanding context ONLY - NOT for generating new content`
      : undefined;

    if (this.isIOSBrowser) {
      if (this.audioQueue.length > 0) {
        this.audioQueue.forEach((item) => {
          if (item.audioUrl.startsWith('blob:')) {
            URL.revokeObjectURL(item.audioUrl);
          }
        });
        this.audioQueue = [];
      }
      this.stopAllAudio();
      this.audioEnabled.set(true);
      this.cdr.markForCheck();
    }

    try {
      await this.bidirectionalConversation.startRecording(
        sourceLang,
        targetLang,
        systemPrompt,
        this.useAdvancedModel
      );

      if (this.isIOSBrowser) {
        this.audioEnabled.set(true);
        this.cdr.markForCheck();
      }

      this.ngZone.run(() => {
        this.recording = true;
        this.updateStatusText();
        this.cdr.markForCheck();
      });
    } catch (err) {
      console.error('[BidirectionalConversationModal] Start failed', err);
      this.errorMessage =
        err instanceof Error ? err.message : 'Failed to start recording';
      setTimeout(() => {
        this.clickedButton = null;
        this.cdr.markForCheck();
      }, 500);
      this.cdr.markForCheck();
    }
  }

  stopRecording(): void {
    this.clickedButton = 'stop';
    this.bidirectionalConversation.stopRecording();
    
    // Clear all pending translations and audio files
    // Stop any currently playing audio
    this.stopAllAudio();
    
    // Clear audio queue and revoke blob URLs
    if (this.audioQueue.length > 0) {
      console.log('[BidirectionalConversationModal] Clearing', this.audioQueue.length, 'pending audio files');
      this.audioQueue.forEach((item) => {
        if (item.audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.audioUrl);
        }
      });
      this.audioQueue = [];
    }
    
    // Clear processed tracking sets
    this.processedTranscriptIndices.clear();
    this.processedAudioTexts.clear();

    this.cdr.markForCheck();
  }

  clearTranscripts(): void {
    this.clickedButton = 'clear';
    this.bidirectionalConversation.clearTranscripts();
    this.cdr.markForCheck();
  }

  dismiss(): void {
    // Set flag to prevent any audio from playing
    this.isDismissing = true;
    this.stopRecording();
    this.stopAllAudio();
    // Clear conversation history when modal is closed
    this.clearTranscripts();
    this.close.emit();
  }

  ngOnDestroy(): void {
    this.stopQueueMonitoring();
    // Set flag to prevent any audio from playing
    this.isDismissing = true;
    this.stopRecording();
    this.stopAllAudio();
    this.recordingSub?.unsubscribe();
    this.transcriptsSub?.unsubscribe();
    this.errorSub?.unsubscribe();
    this.translationSub?.unsubscribe();
  }

  toggleAudio(): void {
    if (
      this.isIOSBrowser &&
      this.audioEnabled() &&
      this.audioQueue.length > 0 &&
      !this.isPlayingAudio
    ) {
      Promise.resolve().then(() => {
        if (
          !this.isPlayingAudio &&
          this.audioQueue.length > 0 &&
          this.audioEnabled()
        ) {
          this.playNextAudio().catch((err) => {
            console.warn('[Bidirectional] Failed to play queued audio:', err);
          });
        }
      });
      this.cdr.markForCheck();
      return;
    }

    const newValue = !this.audioEnabled();
    this.audioEnabled.set(newValue);

    if (!newValue) {
      // Audio disabled - stop all audio and mark all current transcripts as processed
      // so they won't play when audio is re-enabled
      this.stopAllAudio();
      this.transcripts.forEach((t) =>
        this.processedTranscriptIndices.add(t.chunkIndex)
      );
      console.log(
        `[Bidirectional] 🔊 Audio disabled - marked ${this.transcripts.length} transcripts as processed`
      );
    } else {
      // Audio enabled - only process NEW transcripts (not already processed ones)
      // This prevents playing audio for past conversations
      console.log(
        `[Bidirectional] 🔊 Audio enabled - will only process new transcripts`
      );
      if (this.isIOSBrowser) {
        if (this.audioQueue.length > 0 && !this.isPlayingAudio) {
          Promise.resolve().then(() => {
            if (
              !this.isPlayingAudio &&
              this.audioQueue.length > 0 &&
              this.audioEnabled()
            ) {
              this.playNextAudio().catch((err) => {
                console.warn('[Bidirectional] Failed to play audio:', err);
              });
            }
          });
        }
        // Only process new transcripts, not all transcripts
        this.processTranscriptsForAudio();
      } else {
        // Only process new transcripts, not all transcripts
        this.processTranscriptsForAudio();
      }
    }

    this.cdr.markForCheck();
  }

  private processTranscriptsForAudio(): void {
    // When audio is toggled ON, only process transcripts that are truly new
    // (not already in processedTranscriptIndices)
    // This prevents playing audio for past conversations when audio is enabled
    const transcripts = this.transcripts;
    const newTranscripts = transcripts.filter(
      (t) => !this.processedTranscriptIndices.has(t.chunkIndex)
    );
    
    if (newTranscripts.length > 0) {
      console.log(
        `[Bidirectional] 🔊 Processing ${newTranscripts.length} new transcripts for audio (${transcripts.length} total transcripts)`
      );
      this.processNewTranscriptsForAudio(newTranscripts);
    } else {
      console.log(
        `[Bidirectional] 🔊 No new transcripts to process (all ${transcripts.length} transcripts already processed)`
      );
    }
  }

  private async processNewTranscriptsForAudio(
    transcripts: BidirectionalTranscript[]
  ): Promise<void> {
    // Don't process audio if modal is being dismissed
    if (this.isDismissing) {
      console.log('[Bidirectional] 🔊 Skipping audio processing - modal is dismissing');
      return;
    }

    const newTranscripts = transcripts.filter(
      (t) => !this.processedTranscriptIndices.has(t.chunkIndex)
    );

    if (newTranscripts.length === 0) {
      return;
    }

    const audioPromises: Promise<void>[] = [];

    for (const transcript of newTranscripts) {
      if (this.processedTranscriptIndices.has(transcript.chunkIndex)) {
        continue;
      }

      const transcriptText = transcript.text;
      const normalizedText = transcriptText.trim().toLowerCase();

      if (this.processedAudioTexts.has(normalizedText)) {
        this.processedTranscriptIndices.add(transcript.chunkIndex);
        continue;
      }

      this.processedTranscriptIndices.add(transcript.chunkIndex);
      this.processedAudioTexts.add(normalizedText);

      const defaultVoice = 'alloy'; // Default OpenAI voice
      const defaultSpeed = 1.0; // Normal speed

      const audioPromise = this.translatorService
        .tts(transcriptText, defaultSpeed, defaultVoice, this.selectedTargetLanguage)
        .then((audioResult) => {
          if (!audioResult || !audioResult.audioUrl) {
            console.warn('[Bidirectional] TTS result missing audioUrl');
            return;
          }

          if (this.isIOSBrowser) {
            this.audioQueue.push({
              audioUrl: audioResult.audioUrl,
              text: transcriptText,
            });
            console.log(
              `[Bidirectional] 🔊 [iOS Browser] Audio added to queue. Queue length: ${this.audioQueue.length}`
            );
            // Ensure queue processing is active for iOS too
            if (this.isPlayingAudio && this.audioQueue.length > 0) {
              this.ensureQueueProcessing();
              // Also check immediately after a short delay to catch the latest audio
              setTimeout(() => {
                if (this.audioQueue.length > 0 && !this.isPlayingAudio && this.audioEnabled()) {
                  console.log('[Bidirectional] 🔊 [iOS Browser] [Latest Audio Check] Queue has items but not playing - starting playback immediately');
                  this.playNextAudio().catch((err) => {
                    console.error('[Bidirectional] 🔊 [iOS Browser] [Latest Audio Check] Error starting playback:', err);
                    this.isPlayingAudio = false;
                  });
                }
              }, 100);
            }
          } else {
            this.audioQueue.push({
              audioUrl: audioResult.audioUrl,
              text: transcriptText,
            });
            if (!this.isPlayingAudio && this.audioEnabled()) {
              this.playNextAudio().catch((err) => {
                console.error('[Bidirectional] Error playing audio:', err);
                this.isPlayingAudio = false;
                // Retry if queue still has items
                if (this.audioQueue.length > 0 && this.audioEnabled()) {
                  setTimeout(() => this.playNextAudio().catch(console.error), 100);
                }
              });
            } else if (this.isPlayingAudio && this.audioQueue.length > 0) {
              // Audio is already playing, but ensure queue will continue
              this.ensureQueueProcessing();
              // Also check immediately after a short delay to catch the latest audio
              setTimeout(() => {
                if (this.audioQueue.length > 0 && !this.isPlayingAudio && this.audioEnabled()) {
                  console.log('[Bidirectional] 🔊 [Latest Audio Check] Queue has items but not playing - starting playback immediately');
                  this.playNextAudio().catch((err) => {
                    console.error('[Bidirectional] 🔊 [Latest Audio Check] Error starting playback:', err);
                    this.isPlayingAudio = false;
                  });
                }
              }, 100);
            }
          }
        })
        .catch((err) => {
          console.error('[Bidirectional] Error generating audio:', err);
        });

      audioPromises.push(audioPromise);
    }

    await Promise.all(audioPromises);
  }

  private async playNextAudio(): Promise<void> {
    // Don't play audio if modal is being dismissed
    if (this.isDismissing) {
      console.log('[Bidirectional] 🔊 Skipping audio playback - modal is dismissing');
      this.isPlayingAudio = false;
      return;
    }

    if (this.audioQueue.length === 0 || !this.audioEnabled()) {
      return;
    }

    if (this.isPlayingAudio) {
      return;
    }

    this.isPlayingAudio = true;
    const audioItem = this.audioQueue.shift();

    if (!audioItem) {
      this.isPlayingAudio = false;
      return;
    }

    try {
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio.onended = null;
        this.currentAudio.onerror = null;
      }

      this.currentAudio = new Audio(audioItem.audioUrl);
      this.currentAudio.volume = 1.0;
      this.currentAudio.preload = 'auto';

      this.currentAudio.onended = () => {
        URL.revokeObjectURL(audioItem.audioUrl);
        this.currentAudio = undefined;
        this.isPlayingAudio = false;

        if (this.audioQueue.length > 0 && this.audioEnabled()) {
          setTimeout(() => {
            if (!this.isPlayingAudio && this.audioQueue.length > 0 && this.audioEnabled()) {
              this.playNextAudio().catch((err) => {
                console.error('[Bidirectional] Error playing next audio:', err);
                this.isPlayingAudio = false;
                // Retry if queue still has items
                if (this.audioQueue.length > 0 && this.audioEnabled()) {
                  setTimeout(() => this.playNextAudio().catch(console.error), 100);
                }
              });
            } else if (this.audioQueue.length > 0 && !this.isPlayingAudio && this.audioEnabled()) {
              // Double-check: if onended didn't trigger playback, ensure queue processing is active
              console.log('[Bidirectional] 🔊 [onended] Queue still has items but playback not started - ensuring queue processing');
              this.ensureQueueProcessing();
            }
          }, 50);
        } else {
          this.stopQueueMonitoring();
        }
      };

      this.currentAudio.onerror = () => {
        URL.revokeObjectURL(audioItem.audioUrl);
        this.currentAudio = undefined;
        this.isPlayingAudio = false;

        if (this.audioQueue.length > 0 && this.audioEnabled()) {
          setTimeout(() => {
            if (!this.isPlayingAudio && this.audioQueue.length > 0 && this.audioEnabled()) {
              this.playNextAudio().catch((err) => {
                console.error('[Bidirectional] Error playing next audio:', err);
                this.isPlayingAudio = false;
                // Retry if queue still has items
                if (this.audioQueue.length > 0 && this.audioEnabled()) {
                  setTimeout(() => this.playNextAudio().catch(console.error), 100);
                }
              });
            }
          }, 100);
        } else {
          this.stopQueueMonitoring();
        }
      };

      await this.currentAudio.play();
    } catch (error) {
      console.error('[Bidirectional] Error playing audio:', error);
      if (this.currentAudio) {
        URL.revokeObjectURL(audioItem.audioUrl);
        this.currentAudio = undefined;
      }
      this.isPlayingAudio = false;

      if (this.audioQueue.length > 0 && this.audioEnabled()) {
        setTimeout(() => {
          if (!this.isPlayingAudio && this.audioQueue.length > 0 && this.audioEnabled()) {
            this.playNextAudio().catch((err) => {
              console.error('[Bidirectional] Error playing next audio:', err);
              this.isPlayingAudio = false;
              // Retry if queue still has items
              if (this.audioQueue.length > 0 && this.audioEnabled()) {
                setTimeout(() => this.playNextAudio().catch(console.error), 100);
              }
            });
          }
        }, 100);
      } else {
        this.stopQueueMonitoring();
      }
    }
  }

  /**
   * Ensure queue processing continues - monitors queue and starts playback if needed
   */
  private ensureQueueProcessing(): void {
    // Clear any existing interval
    if (this.queueCheckInterval) {
      clearInterval(this.queueCheckInterval);
    }

    // Set up periodic check to ensure queue is being processed
    // Use a shorter interval (300ms) to catch the latest audio more quickly
    this.queueCheckInterval = setInterval(() => {
      if (this.isDismissing) {
        this.stopQueueMonitoring();
        return;
      }

      // If queue has items but nothing is playing, start playback
      if (this.audioQueue.length > 0 && !this.isPlayingAudio && this.audioEnabled()) {
        console.log('[Bidirectional] 🔊 [Queue Monitor] Queue has items but not playing - starting playback');
        this.playNextAudio().catch((err) => {
          console.error('[Bidirectional] 🔊 [Queue Monitor] Error starting playback:', err);
          this.isPlayingAudio = false;
          // Retry if queue still has items
          if (this.audioQueue.length > 0 && this.audioEnabled()) {
            setTimeout(() => this.playNextAudio().catch(console.error), 100);
          }
        });
      } else if (this.audioQueue.length === 0) {
        // Queue is empty, stop monitoring
        this.stopQueueMonitoring();
      }
    }, 300); // Check every 300ms for faster response
  }

  /**
   * Stop queue monitoring
   */
  private stopQueueMonitoring(): void {
    if (this.queueCheckInterval) {
      clearInterval(this.queueCheckInterval);
      this.queueCheckInterval = null;
    }
  }

  private stopAllAudio(): void {
    this.isPlayingAudio = false;

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio.onended = null;
      this.currentAudio.onerror = null;
      this.currentAudio = undefined;
    }

    while (this.audioQueue.length > 0) {
      const item = this.audioQueue.shift();
      if (item && item.audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.audioUrl);
      }
    }
  }

  getLanguageName(languageCode: string): string {
    const languageNames: { [key: string]: string } = {
      en: 'English',
      ko: 'Korean',
      ru: 'Russian',
      ky: 'Kyrgyz',
      'zh-CN': 'Mandarin',
      'zh-HK': 'Cantonese',
      zh: 'Chinese',
      kk: 'Kazakh',
      tg: 'Tajik',
      tk: 'Turkmen',
      uz: 'Uzbek',
      fa: 'Farsi',
      es: 'Spanish',
    };
    return languageNames[languageCode] || languageCode.toUpperCase();
  }

  formatTime(date: Date): string {
    return new Date(date).toLocaleTimeString();
  }

  trackTranscript(index: number, transcript: BidirectionalTranscript): number {
    return transcript.chunkIndex;
  }

  getTranslationDirectionLabel(direction: string): string {
    switch (direction) {
      case 'source-to-target':
        return '→';
      case 'target-to-source':
        return '←';
      default:
        return '•';
    }
  }

  isWordLimitError(errorMessage: string): boolean {
    if (!errorMessage) return false;
    const lower = errorMessage.toLowerCase();
    return (
      lower.includes('word limit') ||
      lower.includes('words used') ||
      lower.includes('buy more') ||
      lower.includes('purchase')
    );
  }

  onBuyWords(): void {
    this.showBuyWords.emit();
  }

  dismissError(): void {
    this.errorMessage = '';
  }
}

